import React, { useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  alpha,
  useTheme,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Link,
  IconButton,
  Collapse,
  Stack,
  Tooltip,
  Button,
  Grid,
} from '@mui/material';
import {
  OpenInNew as OpenInNewIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { useEbayPricing, useGradedPricing } from '../../hooks/useEbayPricing';
import dayjs from 'dayjs';

interface EbayMarketplaceProps {
  cardName: string;
  setId: string;
  cardNumber: string;
  enabled?: boolean;
  searchQuery?: string;
}

const EbayMarketplace: React.FC<EbayMarketplaceProps> = ({
  cardName,
  setId,
  cardNumber,
  enabled = true,
  searchQuery,
}) => {
  const theme = useTheme();
  const [ebayTab, setEbayTab] = useState(0);
  const [subTab, setSubTab] = useState(0); // 0: Sold, 1: Active
  const [language, setLanguage] = useState<'English' | 'Japanese' | 'All'>('All');
  const [visibleCount, setVisibleCount] = useState(10);

  React.useEffect(() => {
    setVisibleCount(10);
  }, [ebayTab, subTab, language]);

  const searchParams = { 
    cardName: language === 'All' ? cardName : `${cardName} ${language}`,
    cardNumber, 
    setId 
  };

  const {
    data: ebayData,
    isLoading: ebayLoading,
    error: ebayError,
  } = useEbayPricing(searchParams, enabled);

  const {
    data: gradedData,
    isLoading: gradedLoading,
  } = useGradedPricing(searchParams, enabled && (ebayTab === 1 || ebayTab === 2));

  // Auto-switch to tab with listings
  React.useEffect(() => {
    if (!ebayLoading && ebayData) {
      const listings = getListingsForTab();
      if (subTab === 0 && listings.sold.length === 0 && listings.active.length > 0) {
        setSubTab(1);
      } else if (subTab === 1 && listings.active.length === 0 && listings.sold.length > 0) {
        setSubTab(0);
      }
    }
  }, [ebayLoading, ebayData, ebayTab]);

  const getCategory = (title: string) => {
    const upper = title.toUpperCase();
    if (upper.includes('PSA 10') || upper.includes('PSA10')) return 'PSA 10';
    if (upper.includes('PSA 9') || upper.includes('PSA9')) return 'PSA 9';
    if (!upper.includes('PSA') && !upper.includes('BGS') && !upper.includes('CGC') && !upper.includes('SGC')) return 'Ungraded';
    return 'Other';
  };

  const categorizeListings = (listings: any[] | undefined) => {
    if (!listings) return { ungraded: [], psa10: [], psa9: [] };
    const ungraded: any[] = [];
    const psa10: any[] = [];
    const psa9: any[] = [];
    listings.forEach(l => {
      const cat = getCategory(l.title);
      if (cat === 'Ungraded') ungraded.push(l);
      else if (cat === 'PSA 10') psa10.push(l);
      else if (cat === 'PSA 9') psa9.push(l);
    });
    return { ungraded, psa10, psa9 };
  };

  const activeCategorized = categorizeListings(ebayData?.ebay.activeListings);
  const soldCategorized = categorizeListings(ebayData?.ebay.soldListings);

  const getListingsForTab = () => {
    if (ebayTab === 1 && gradedData) {
      const psa10Data = gradedData.graded.find(g => g.grade === 'PSA 10');
      return { active: psa10Data?.activeListings || [], sold: psa10Data?.soldListings || [], label: 'PSA 10' };
    }
    if (ebayTab === 2 && gradedData) {
      const psa9Data = gradedData.graded.find(g => g.grade === 'PSA 9');
      return { active: psa9Data?.activeListings || [], sold: psa9Data?.soldListings || [], label: 'PSA 9' };
    }
    switch(ebayTab) {
      case 0: return { active: activeCategorized.ungraded, sold: soldCategorized.ungraded, label: 'Ungraded' };
      case 1: return { active: activeCategorized.psa10, sold: soldCategorized.psa10, label: 'PSA 10' };
      case 2: return { active: activeCategorized.psa9, sold: soldCategorized.psa9, label: 'PSA 9' };
      default: return { active: [], sold: [], label: '' };
    }
  };

  const currentTabListings = getListingsForTab();

  // Get summary stats for current grade tab
  const getSummaryStats = () => {
    if (ebayTab === 0 && ebayData) {
      return { avg: ebayData.ebay.averagePrice, low: ebayData.ebay.lowestPrice, high: ebayData.ebay.highestPrice };
    }
    if ((ebayTab === 1 || ebayTab === 2) && gradedData) {
      const grade = ebayTab === 1 ? 'PSA 10' : 'PSA 9';
      const d = gradedData.graded.find(g => g.grade === grade);
      if (d) return { avg: d.averagePrice, low: d.lowestPrice, high: d.highestPrice };
    }
    return null;
  };

  const stats = getSummaryStats();

  const ListingRow = ({ listing, isSold }: { listing: any; isSold: boolean }) => {
    const [open, setOpen] = useState(false);

    return (
      <Box sx={{ borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}` }}>
        <Box 
          sx={{ 
            py: 1.5, 
            px: 1, 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1.5,
            cursor: 'pointer',
            transition: 'background-color 0.2s',
            '&:hover': { bgcolor: alpha(theme.palette.action.hover, 0.04) } 
          }} 
          onClick={() => setOpen(!open)}
        >
          {/* Expand Icon - Desktop Only */}
          <Box sx={{ width: 32, display: { xs: 'none', sm: 'flex' }, justifyContent: 'center' }}>
            <IconButton size="small" sx={{ p: 0.25 }}>
              {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
            </IconButton>
          </Box>
          
          {/* Title and Mobile Info */}
          <Box sx={{ flexGrow: 1, minWidth: 0, overflow: 'hidden' }}>
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: 500, 
                fontSize: { xs: '0.75rem', sm: '0.8rem' },
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                lineHeight: 1.3
              }}
            >
              {listing.title}
            </Typography>
            {/* Mobile-only sub-info */}
            <Box sx={{ display: { xs: 'flex', sm: 'none' }, gap: 1.5, mt: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                {isSold 
                  ? (listing.soldDate ? dayjs(listing.soldDate).format('MMM D') : '—')
                  : (listing.listedDate ? dayjs(listing.listedDate).format('MMM D') : '—')
                }
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 700, color: isSold ? 'success.main' : 'text.primary' }}>
                ${listing.price.toFixed(2)}
              </Typography>
            </Box>
          </Box>

          {/* Desktop Date Column */}
          <Box sx={{ display: { xs: 'none', sm: 'block' }, textAlign: 'right', width: 70, flexShrink: 0 }}>
            <Typography variant="caption" color="text.secondary">
              {isSold 
                ? (listing.soldDate ? dayjs(listing.soldDate).format('MMM D') : '—')
                : (listing.listedDate ? dayjs(listing.listedDate).format('MMM D') : '—')
              }
            </Typography>
          </Box>

          {/* Desktop Price Column */}
          <Box sx={{ display: { xs: 'none', sm: 'block' }, textAlign: 'right', width: 80, flexShrink: 0 }}>
            <Typography variant="body2" fontWeight={700} color={isSold ? 'success.main' : 'text.primary'}>
              ${listing.price.toFixed(2)}
            </Typography>
          </Box>

          {/* Action Column */}
          <Box sx={{ width: 40, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
            <Link href={listing.listingUrl} target="_blank" rel="noopener" onClick={(e) => e.stopPropagation()}>
              <OpenInNewIcon sx={{ fontSize: 16, color: 'text.secondary', opacity: 0.6, '&:hover': { opacity: 1 } }} />
            </Link>
          </Box>
        </Box>

        <Collapse in={open} timeout="auto" unmountOnExit>
          <Box sx={{ pb: 2, px: 2, ml: { xs: 0, sm: 5 } }}>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>Seller</Typography>
                <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>{listing.sellerName || 'Private'}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>Condition</Typography>
                <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>{listing.condition || '—'}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>Shipping</Typography>
                <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                  {!listing.shippingCost || listing.shippingCost === '0.00' || listing.shippingCost === '0' 
                    ? 'Free' 
                    : listing.shippingCost === 'Unknown' ? '—' : `$${listing.shippingCost}`}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>Feedback</Typography>
                <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                  {listing.sellerFeedbackScore > 0 
                    ? `${listing.sellerFeedbackScore.toLocaleString()} (${listing.sellerFeedbackPercentage})` 
                    : '—'}
                </Typography>
              </Grid>
              {listing.itemLocation && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>Location</Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>{listing.itemLocation}</Typography>
                </Grid>
              )}
            </Grid>
            <Button 
              component="a"
              href={listing.listingUrl}
              target="_blank"
              variant="outlined"
              size="small"
              fullWidth
              sx={{ mt: 2, display: { xs: 'flex', sm: 'none' } }}
              endIcon={<OpenInNewIcon />}
            >
              View on eBay
            </Button>
          </Box>
        </Collapse>
      </Box>
    );
  };

  const renderListings = (listings: any[], isSold: boolean) => {
    if (!listings || listings.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center', bgcolor: alpha(theme.palette.background.paper, 0.4), borderRadius: 2 }}>
          No listings found
        </Typography>
      );
    }

    return (
      <Box sx={{ mt: 1 }}>
        {/* Header - Aligned with Row columns */}
        <Box 
          sx={{ 
            px: 1, 
            pb: 1, 
            display: { xs: 'none', sm: 'flex' }, 
            alignItems: 'center', 
            gap: 1.5,
            borderBottom: `1px solid ${theme.palette.divider}`,
            opacity: 0.7
          }}
        >
          <Box sx={{ width: 32 }} /> {/* Space for Icon */}
          <Typography variant="caption" sx={{ fontWeight: 700, flexGrow: 1 }}>TITLE</Typography>
          <Typography variant="caption" sx={{ fontWeight: 700, width: 70, textAlign: 'right' }}>{isSold ? 'SOLD' : 'LISTED'}</Typography>
          <Typography variant="caption" sx={{ fontWeight: 700, width: 80, textAlign: 'right' }}>PRICE</Typography>
          <Box sx={{ width: 40 }} /> {/* Space for Link Icon */}
        </Box>
        
        <Box>
          {listings.slice(0, visibleCount).map((listing, index) => (
            <ListingRow key={index} listing={listing} isSold={isSold} />
          ))}
        </Box>

        {listings.length > visibleCount && (
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
            <Button 
              variant="outlined"
              size="small" 
              onClick={() => setVisibleCount(prev => prev + 10)}
              sx={{ 
                borderRadius: 20, 
                px: 3,
                color: 'text.secondary',
                borderColor: theme.palette.divider,
                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) }
              }}
            >
              Show more
            </Button>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box>
      {/* Grade tabs */}
      <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
        {['Ungraded', 'PSA 10', 'PSA 9'].map((label, i) => (
          <Chip
            key={label}
            label={label}
            onClick={() => setEbayTab(i)}
            size="small"
            sx={{
              fontWeight: 600,
              fontSize: '0.7rem',
              cursor: 'pointer',
              bgcolor: ebayTab === i ? alpha(theme.palette.primary.main, 0.15) : 'transparent',
              color: ebayTab === i ? theme.palette.primary.main : 'text.secondary',
              border: `1px solid ${ebayTab === i ? alpha(theme.palette.primary.main, 0.3) : theme.palette.divider}`,
            }}
          />
        ))}
        {/* Language filter */}
        <Box sx={{ flexGrow: 1 }} />
        {(['All', 'EN', 'JP'] as const).map((lang) => {
          const fullLang = lang === 'EN' ? 'English' : lang === 'JP' ? 'Japanese' : 'All';
          return (
            <Chip
              key={lang}
              label={lang}
              size="small"
              onClick={() => setLanguage(fullLang as any)}
              sx={{
                fontWeight: 500,
                fontSize: '0.65rem',
                cursor: 'pointer',
                bgcolor: language === fullLang ? alpha(theme.palette.text.secondary, 0.12) : 'transparent',
                color: language === fullLang ? 'text.primary' : 'text.secondary',
                border: `1px solid ${theme.palette.divider}`,
                minWidth: 32,
              }}
            />
          );
        })}
      </Stack>

      {ebayLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <Box>
          {/* Inline stats */}
          {stats && (
            <Box sx={{ display: 'flex', gap: 3, mb: 2, py: 1, borderBottom: `1px solid ${theme.palette.divider}` }}>
              {[
                { label: 'Avg', value: stats.avg },
                { label: 'Low', value: stats.low },
                { label: 'High', value: stats.high },
              ].map(({ label, value }) => (
                <Box key={label}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {label}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    ${value?.toFixed(2) || '—'}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {/* Sold / Active toggle */}
          <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
            {[
              { label: `Sold (${currentTabListings.sold.length})`, idx: 0 },
              { label: `Active (${currentTabListings.active.length})`, idx: 1 },
            ].map(({ label, idx }) => (
              <Typography
                key={idx}
                variant="body2"
                onClick={() => setSubTab(idx)}
                sx={{
                  cursor: 'pointer',
                  fontWeight: subTab === idx ? 600 : 400,
                  color: subTab === idx ? 'text.primary' : 'text.secondary',
                  borderBottom: subTab === idx ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
                  pb: 0.5,
                  fontSize: '0.8rem',
                  '&:hover': { color: 'text.primary' },
                }}
              >
                {label}
              </Typography>
            ))}
          </Stack>

          {subTab === 0
            ? renderListings(currentTabListings.sold, true)
            : renderListings(currentTabListings.active, false)
          }
        </Box>
      )}
    </Box>
  );
};

export default EbayMarketplace;
